import React, { useState } from 'react';
import { Menu } from 'react-native-paper';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useController } from 'react-hook-form';

import ChevronDown from '@common/components/Icons/ChevronDown';
import { FormGroupSelectProps } from '@nocode/types/formGroup.type';
import { isCanvas } from '@common/utils';

const FormGroupSelect = ({
  name,
  options,
  placeholder,
  styleConfig,
  maxMenuHeight,
}: FormGroupSelectProps & { maxMenuHeight?: number }) => {
  const style = createStyle(styleConfig, maxMenuHeight);
  const [open, setOpen] = useState(false);
  const canvas = isCanvas() && Platform.OS === 'web';

  const {
    field,
    fieldState: { invalid, isTouched, isDirty },
    formState: { touchedFields, dirtyFields },
  } = useController({
    name,
  });

  if (canvas) {
    return (
      <View style={style.wrapper}>
        <Text style={style.text}>
          {options.find((item) => item?.value === field.value)?.label ||
            placeholder}
        </Text>

        <ChevronDown
          style={{
            transform: [{ rotate: open ? '-180deg' : '0deg' }],
          }}
        />
      </View>
    );
  }

  return (
    <Menu
      visible={open}
      anchor={
        <Pressable style={style.wrapper} onPress={() => setOpen(true)}>
          <Text style={style.text}>
            {options.find((item) => item.value === field.value)?.label ||
              placeholder}
          </Text>

          <ChevronDown
            style={{
              transform: [{ rotate: open ? '-180deg' : '0deg' }],
            }}
          />
        </Pressable>
      }
      onDismiss={() => setOpen(false)}
      style={style.menuWrapper}
      contentStyle={style.menuItem}
    >
      <ScrollView style={maxMenuHeight ? { maxHeight: maxMenuHeight } : {}}>
        {options.map((item) => (
          <Menu.Item
            key={item.label}
            title={item.label}
            onPress={() => {
              field.onChange(item.value);
              setOpen(false);
            }}
            style={style.menuItemWrapper}
            contentStyle={style.menuItem}
            titleStyle={style.text}
          />
        ))}
      </ScrollView>
    </Menu>
  );
};

const createStyle = (
  styleConfig: FormGroupSelectProps['styleConfig'],
  maxMenuHeight?: number
) => {
  const width = styleConfig?.size?.width ? Number(styleConfig.size.width) : 360;
  const height = styleConfig?.size?.height || 40;

  return StyleSheet.create({
    wrapper: {
      width,
      height,
      borderColor: styleConfig?.borderColor || '#E9EFF2',
      borderRadius: styleConfig?.borderRadius || 4,
      position: 'relative',
      paddingHorizontal: 16,
      borderWidth: 1,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    text: {
      color: styleConfig?.color,
      fontSize: styleConfig?.fontSize || 12,
    },
    menuWrapper: {
      width,
      marginTop: 41 + Number(height),
      ...(maxMenuHeight ? { maxHeight: maxMenuHeight } : {}),
    },
    menuItemWrapper: {
      width,
      maxWidth: width,
    },
    menuItem: {
      width,
      ...(maxMenuHeight ? { maxHeight: maxMenuHeight } : {}),
    },
  });
};

export default FormGroupSelect;
